/**
 * "Deploy to production": queues `publish` on the running forge (202); the
 * runtime submits the workspace through the normal publish pipeline and the
 * page follows the submission (`api.getPluginSubmission`) once the forge
 * surfaces its id.
 *
 * "Copy test data to production" (ADR-023 D4) is a per-publish choice,
 * default off: the platform copies the test channel's DataStore rows and
 * files into the live arche after the submission goes through — only into
 * an arche that has no data yet (a re-publish never touches live data).
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Rocket } from 'lucide-react';
import type { PluginSubmissionDetailResult } from '@/lib/api';
import type { ForgeSummary } from '../types';

export interface ForgePublishOptions {
  carryTestData: boolean;
}

interface ForgePublishPanelProps {
  forge: ForgeSummary;
  submission: PluginSubmissionDetailResult | null;
  onPublish: (options: ForgePublishOptions) => void;
  canPublish: boolean;
  isPublishRequested: boolean;
}

function submissionLine(submission: PluginSubmissionDetailResult | null): string | null {
  if (!submission) return null;
  const s = submission.submission;
  if (s.errorMessage) return `${s.status}: ${s.errorMessage}`;
  return `Submission ${s.status.replace(/_/g, ' ')}`;
}

export function ForgePublishPanel({
  forge,
  submission,
  onPublish,
  canPublish,
  isPublishRequested,
}: ForgePublishPanelProps) {
  const [carryTestData, setCarryTestData] = useState(false);
  const line = submissionLine(submission);
  const controlsDisabled = !canPublish || isPublishRequested;
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="forge-publish-panel">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Production</h3>
        <Button size="sm" onClick={() => onPublish({ carryTestData })} disabled={controlsDisabled}>
          {isPublishRequested ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Rocket className="h-4 w-4 mr-1" />
          )}
          Deploy to production
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Submits the workspace through the normal publish pipeline (security review, build), then the
        arche goes live at <code className="font-mono">/a/{forge.archeId}</code>.
      </p>
      <div className="flex items-start gap-2">
        <Checkbox
          id="forge-publish-carry-test-data"
          checked={carryTestData}
          onCheckedChange={setCarryTestData}
          disabled={controlsDisabled}
          aria-describedby="forge-publish-carry-test-data-help"
        />
        <div className="space-y-0.5">
          <label htmlFor="forge-publish-carry-test-data" className="text-xs font-medium">
            Copy test data to production
          </label>
          <p id="forge-publish-carry-test-data-help" className="text-xs text-muted-foreground">
            Seeds the live arche with the test build&apos;s data store rows and files. Only an arche
            with no data yet is seeded; a later publish never changes live data.
          </p>
        </div>
      </div>
      {isPublishRequested && !line && (
        <p className="text-xs text-muted-foreground">Packaging the workspace…</p>
      )}
      {line && <p className="text-xs">{line}</p>}
    </div>
  );
}
